import { FC, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Box, Flex, useBreakpointValue } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';

import { PackageType } from '__generated__/GQL';
import cashImg from 'assets/brand/cash.png';
import docImg from 'assets/brand/doc.png';
import folderImg from 'assets/brand/folder.png';
import asBentoPage from 'common/bento/hoc/asBentoPage/asBentoPage';
import Button from 'components/_core/Button';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import RebrandedPrice from 'components/Price/rebranded';
import SuspenseQuery from 'components/SuspenseQuery';
import amountWithMinorCurrency from 'features/Onboarding/modules/PricingPlan/helpers/amountWithMinorCurrency';

import { TopBar } from '../../components/StepsLayout';
import {
  OnboardingSasuOffersDocument,
  OnboardingSasuOffersQuery,
} from '../../graphql/queries/onboardingSasuOffers.gql';
import { PresentationColumn } from './components/PresentationColumn';
import locales from './locales';

type SasuAddon =
  OnboardingSasuOffersQuery['viewer']['company']['onboardingSasuAddons'][0];

interface CreationPresentationProps {
  addons: SasuAddon[];
  onContinue: () => void;
}

const CreationPresentation: FC<CreationPresentationProps> = ({
  addons,
  onContinue,
}) => {
  const contract = useBreakpointValue({
    base: locales.contract.short,
    lg: locales.contract.long,
  });

  const {
    'capital-deposit': capitalDepositAddon,
    'sasu-administrative-fees': administrativeFeesAddon,
    'sasu-formalities': sasuFormalitiesAddon,
  } = useMemo(
    () =>
      addons.reduce(
        (obj, { providerAddonId, ...rest }) => ({
          ...obj,
          [providerAddonId]: rest,
        }),
        {} as {
          'capital-deposit'?: Omit<SasuAddon, 'providerAddonId'>;
          'sasu-administrative-fees'?: Omit<SasuAddon, 'providerAddonId'>;
          'sasu-formalities'?: Omit<SasuAddon, 'providerAddonId'>;
        },
      ),
    [addons],
  );

  if (
    !capitalDepositAddon ||
    !administrativeFeesAddon ||
    !sasuFormalitiesAddon
  ) {
    throw new Error('Missing addon information');
  }

  const totalAmount =
    sasuFormalitiesAddon.basePriceWithoutVAT +
    administrativeFeesAddon.basePriceWithoutVAT +
    capitalDepositAddon.basePriceWithoutVAT;

  return (
    <>
      <Heading
        display={{
          base: 'none',
          lg: 'block',
        }}
        marginBottom="space-40"
        size="lg"
      >
        {locales.title}
      </Heading>
      <TopBar.Title>{locales.title}</TopBar.Title>
      <SunshineCard padding={0}>
        <Flex flexDirection={{ base: 'column', lg: 'row' }}>
          <PresentationColumn
            imgSrc={docImg}
            items={[
              locales.statutesWriting,
              locales.legalAnnounce,
              locales.verification,
              locales.sending,
            ]}
            price={amountWithMinorCurrency(
              sasuFormalitiesAddon.basePriceWithoutVAT,
            )}
            title={locales.formalitiesLabel}
          />
          <PresentationColumn
            description={locales.adminFeesDescription}
            imgSrc={folderImg}
            items={[locales.legalAnnounceFees, locales.registrationFees]}
            price={amountWithMinorCurrency(
              administrativeFeesAddon.basePriceWithoutVAT,
            )}
            title={locales.adminFeesLabel}
          />
          <PresentationColumn
            description={locales.capitalDepositDescription}
            imgSrc={cashImg}
            items={[locales.capitalDeposit, locales.certificate, locales.funds]}
            price={amountWithMinorCurrency(
              capitalDepositAddon.basePriceWithoutVAT,
            )}
            title={locales.capitalDepositLabel}
          />
        </Flex>
      </SunshineCard>
      <Flex
        alignSelf="center"
        background={{
          base: 'linear-gradient(0deg, rgba(255,255,255,1) 0%, rgba(255,255,255,.6) 80%, rgba(255,255,255,0) 100%)',
          md: 'none',
        }}
        bottom="0"
        flexDirection="column"
        marginBottom={{
          base: 0,
          md: 'space-24',
        }}
        marginTop="space-16"
        paddingY={{
          base: 'space-24',
          md: 0,
        }}
        position={{ base: 'sticky', md: 'initial' }}
        width="full"
      >
        <SunshineCard background="grey.100">
          <Flex alignItems="center" justifyContent="space-between">
            <Box>
              <Text>{locales.total}</Text>
              <Text
                fontSize="font-18"
                fontWeight="weight-500"
                variant="primary"
              >
                {contract}
              </Text>
            </Box>
            <Flex alignItems="flex-end">
              <RebrandedPrice
                size="xl"
                value={amountWithMinorCurrency(totalAmount)}
                withoutCents
              />
              <Text fontSize="font-13" marginLeft="space-6">
                {locales.excludingVAT}
              </Text>
            </Flex>
          </Flex>
        </SunshineCard>
        <Flex
          justifyContent="center"
          marginTop={{
            base: 'space-20',
            lg: 'space-56',
          }}
        >
          <Button isExpanded onClick={onContinue}>
            {locales.continue}
          </Button>
        </Flex>
      </Flex>
    </>
  );
};

interface CreationPresentationContainerProps
  extends Omit<CreationPresentationProps, 'addons'> {
  companyProfileId: string;
}

const CreationPresentationContainer =
  asBentoPage<CreationPresentationContainerProps>(
    ({ companyProfileId, onPrev, ...props }) => {
      const query = useQuery(OnboardingSasuOffersDocument, {
        variables: {
          companyProfileId,
          input: {
            type: PackageType.CapitalDeposit,
          },
        },
      });

      return (
        <Box
          maxWidth="972px"
          paddingLeft="space-16"
          paddingRight="space-16"
          paddingTop="space-32"
          width="100%"
        >
          <AnimatePresence>
            <motion.div exit={{ opacity: 0, y: 10 }}>
              <SuspenseQuery query={query}>
                {({
                  viewer: {
                    company: { onboardingSasuAddons },
                  },
                }) => (
                  <motion.div
                    animate={{ opacity: 1, y: 0 }}
                    initial={{ opacity: 0, y: 20 }}
                    transition={{ delay: 0.3 }}
                  >
                    <CreationPresentation
                      {...props}
                      addons={onboardingSasuAddons}
                    />
                  </motion.div>
                )}
              </SuspenseQuery>
            </motion.div>
          </AnimatePresence>
        </Box>
      );
    },
  );

export default CreationPresentationContainer;
